import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';

const HISTORY_LENGTH_LIMIT = 10;

/**
 * Allows to return back from the page.
 *
 * Usage:
 * 1) call `init(...)` in the ngOnInit of the page where you want to use this service
 * 2.1) simply call `goBack()` to go back
 * 2.2) for <a> tags also use the Router directives, like this:
 * ```
 * <a [routerLink]="backRouterLink" [queryParams]="backQueryParams" (click)="goBack()">...
 * ```
 */
@Injectable()
export class GoBackService {
  private history: {routerLink: any[], queryParams: {[key: string]: string}}[] = [];

  private defaultRouterLink: any[];
  private defaultQueryParams;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
  ) {
    this.router.events
      .filter(event => event instanceof NavigationEnd)
      .subscribe((event: NavigationEnd) => {
        // Prevent double url encoding
        const url = decodeURI(event.urlAfterRedirects);
        this.history.unshift({
          routerLink: [GoBackService.splitUrlAndQueryParams(url).url],
          queryParams: this.route.snapshot.queryParams,
        });
        if (this.history.length > HISTORY_LENGTH_LIMIT) {
          this.history.length = HISTORY_LENGTH_LIMIT;
        }
      });
  }

  static splitUrlAndQueryParams(url: string) {
    const qPos = url.indexOf('?');
    if (qPos >= 0) {
      return { url: url.substring(0, qPos), query: url.substring(qPos + 1) };
    } else {
      return { url, query: '' };
    }
  }

  init(defaultRouterLink, defaultQueryParams?) {
    this.defaultRouterLink = defaultRouterLink;
    this.defaultQueryParams = defaultQueryParams || {};
  }

  get backRouterLink() {
    if (this.history.length > 1) {
      return this.history[1].routerLink;
    }
    return this.defaultRouterLink;
  }

  get backQueryParams() {
    if (this.history.length > 1) {
      return this.history[1].queryParams;
    }
    return this.defaultQueryParams;
  }

  goBack() {
    const routerLink = this.backRouterLink;
    const queryParams = this.backQueryParams;
    if (this.history.length > 1) {
      this.history.shift();
    }
    this.router.navigate(routerLink, { queryParams });
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/go-back.service.ts